import { z } from "zod";
import type { Operation } from "../types";

export interface AgentActivityRun {
  id: number;
  role: string;
  agent_id: string;
  source: "worker" | "verify";
  started_at: string;
}

export interface AgentActivity {
  running: number;
  runs: AgentActivityRun[];
}

const activityResponse = z.object({
  running: z.number(),
  runs: z.array(
    z.object({
      id: z.number(),
      role: z.string(),
      agent_id: z.string(),
      source: z.enum(["worker", "verify"]),
      started_at: z.string(),
    }),
  ),
});

const activityOp: Operation<void, void, void, AgentActivity> = {
  method: "GET",
  path: () => "/api/agents/activity",
  response: activityResponse,
};

export interface SectionAgentEntry {
  agent_id: string;
  role: string;
  task_id: number;
  task_title: string;
  project_slug: string;
  started_at: string;
}

export interface SectionActivity {
  sections: Array<{ slug: string; agents: SectionAgentEntry[] }>;
}

const sectionActivityResponse = z.object({
  sections: z.array(
    z.object({
      slug: z.string(),
      agents: z.array(
        z.object({
          agent_id: z.string(),
          role: z.string(),
          task_id: z.number(),
          task_title: z.string(),
          project_slug: z.string(),
          started_at: z.string(),
        }),
      ),
    }),
  ),
});

const sectionActivityOp: Operation<void, void, void, SectionActivity> = {
  method: "GET",
  path: () => "/api/agents/section-activity",
  response: sectionActivityResponse,
};

/* ──────────────────────────────────────────────────────────────────────────────
 * RO-2-11 — three actions from the `other` domain that belong to the /agents
 * plane rather than to a goal:
 *
 *   `spawnWorkerNow`      src/agents/actions.ts   (the 9th of that file's 9;
 *                          the other 8 are goal-scoped → ./agent-goals)
 *   `verifyActionFromUi`  src/actions/actions.ts   ┐ the review inbox's two
 *   `rejectActionFromUi`  src/actions/actions.ts   ┘ operator verdicts
 *
 * They share this namespace because they share a SUBJECT — the agent plane's own
 * rows (`worker_runs` via a detached run, `excellent_agent_actions`) — which is
 * also the boundary `/api/agents/*` already draws for `activity` above.
 *
 * Live Next call sites stay on their server actions (`ccb89192`): `_review-inbox
 * .tsx` and `_unified-list.tsx` for the two verdicts. Both are among the five
 * shared-render files this item exists to unblock (RO-3-00f/g).
 * ────────────────────────────────────────────────────────────────────────────── */

/**
 * `spawnWorkerNow` — the on-demand "Run a worker" button. DETACHED: `ok:true`
 * means the child was spawned, never that the run finished. The three refusals
 * are kept as a discriminated union because the UI renders a different message
 * for each: an unknown role, a role already running (lockfile present), or a
 * spawn-time crash.
 */
const workerSpawnBody = z.object({ role: z.string() });
export type AgentWorkerSpawnBody = z.infer<typeof workerSpawnBody>;

export type AgentWorkerSpawnResult =
  | { ok: true }
  | { ok: false; reason: "unknown-role" | "locked" | "spawn-failed"; detail?: string };

const workerSpawnOp: Operation<void, void, AgentWorkerSpawnBody, AgentWorkerSpawnResult> = {
  method: "POST",
  path: () => "/api/agents/workers/spawn",
  body: workerSpawnBody,
  response: z.custom<AgentWorkerSpawnResult>(),
  // detached spawn bumps activity.running + runs[].
  invalidates: [activityOp, sectionActivityOp],
};

/**
 * `verifyActionFromUi` — clear a proposed agent action as the HUMAN operator.
 *
 * `verified` is distinct from `ok`: `ok` says the verdict was recorded, `verified`
 * says the gate is now MET (a policy needing two verifiers records the first vote
 * with `ok:true, verified:false`). `reason` is the gate's own verdict string, so
 * it stays a free-form `string` — `evaluateActionGate` owns that vocabulary and
 * an enum here would fork it.
 */
export type AgentActionVerifyResult = { ok: boolean; verified: boolean; reason: string };

// invalidation-ignore: no action-queue Operation — activity/sectionActivity are worker runs, not excellent_agent_actions
const actionVerifyOp: Operation<{ uid: string }, void, void, AgentActionVerifyResult> = {
  method: "POST",
  path: ({ uid }) => `/api/agents/actions/${encodeURIComponent(uid)}/verify`,
  params: z.object({ uid: z.string().min(1) }),
  response: z.object({ ok: z.boolean(), verified: z.boolean(), reason: z.string() }),
};

/**
 * `rejectActionFromUi` — reject a proposed agent action as the human operator.
 * The note is optional in the source action and stays optional here (it is stored
 * as NULL when absent, not as an empty string).
 */
const actionRejectBody = z.object({ note: z.string().optional() });
export type AgentActionRejectBody = z.infer<typeof actionRejectBody>;

// invalidation-ignore: no action-queue Operation — activity/sectionActivity are worker runs, not excellent_agent_actions
const actionRejectOp: Operation<{ uid: string }, void, AgentActionRejectBody, { ok: boolean }> = {
  method: "POST",
  path: ({ uid }) => `/api/agents/actions/${encodeURIComponent(uid)}/reject`,
  params: z.object({ uid: z.string().min(1) }),
  body: actionRejectBody,
  response: z.object({ ok: z.boolean() }),
};

export const agents = {
  activity: activityOp,
  sectionActivity: sectionActivityOp,
  // RO-2-11
  workerSpawn: workerSpawnOp,
  actionVerify: actionVerifyOp,
  actionReject: actionRejectOp,
};
